import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';

interface YtVideo {
  id: number;
  videoId: string;
  title: string | null;
  channelTitle: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  viewCount: number | null;
  commentCount: number | null;
  matchedKeywords: string | null;
  sentimentLabel: string | null;
  riskLevel: string | null;
  riskScore: number | null;
  trendTopic: string | null;
  status: string;
  isWhitelisted: boolean;
  isBlacklisted: boolean;
  lastSyncedAt: string | null;
}

interface YtComment {
  id: number;
  commentId: string;
  videoId: string;
  authorName: string | null;
  commentText: string;
  likeCount: number | null;
  publishedAt: string | null;
  matchedKeywords: string | null;
  sentimentLabel: string | null;
  sentimentScore: number | null;
  intentLabel: string | null;
  riskLevel: string | null;
  riskScore: number | null;
  status: string;
}

interface YtStats {
  totalVideos: number;
  totalComments: number;
  highRisk: number;
  negative: number;
  positive: number;
  complaints: number;
  openVideos: number;
  whitelisted: number;
  blacklisted: number;
  topTrends: { trendKey: string; trendTopic: string; count: number }[];
}

interface YtConfig {
  keywords: string[];
  regionCode: string;
  maxResults: number;
  fetchIntervalMs: number;
  includeTerms: string[];
  excludeTerms: string[];
  enabled: boolean;
  updatedAt: string | null;
}

@Component({
  selector: 'app-youtube-monitoring',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './youtube-monitoring.component.html',
  styleUrls: ['./youtube-monitoring.component.scss'],
})
export class YoutubeMonitoringComponent implements OnInit, OnDestroy {
  activeTab: 'videos' | 'comments' | 'config' = 'videos';

  videos: YtVideo[] = [];
  comments: YtComment[] = [];

  stats: YtStats = {
    totalVideos: 0, totalComments: 0, highRisk: 0,
    negative: 0, positive: 0, complaints: 0, openVideos: 0,
    whitelisted: 0, blacklisted: 0, topTrends: [],
  };

  videoPage = 0; videoTotal = 0; videoTotalPages = 0;
  commentPage = 0; commentTotal = 0; commentTotalPages = 0;
  readonly pageSize = 20;

  vFilterStatus = ''; vFilterSentiment = ''; vFilterRisk = '';
  vFilterKeyword = ''; vFilterFrom = ''; vFilterTo = ''; vFilterQ = '';
  vFilterWhitelisted: '' | 'true' | 'false' = '';
  vFilterBlacklisted: '' | 'true' | 'false' = '';

  cFilterStatus = ''; cFilterSentiment = ''; cFilterIntent = '';
  cFilterRisk = ''; cFilterVideoId = ''; cFilterFrom = ''; cFilterTo = ''; cFilterQ = '';

  videosLoading = true; commentsLoading = true;
  syncing = false; syncResult: string | null = null;
  errorMessage = '';

  expandedVideoId: number | null = null;
  expandedCommentId: number | null = null;
  reanalyzing = false;
  reanalyzeResult: string | null = null;

  // Config panel state
  config: YtConfig = {
    keywords: [], regionCode: 'PK', maxResults: 15,
    fetchIntervalMs: 14400000, includeTerms: [], excludeTerms: [],
    enabled: true, updatedAt: null,
  };
  configLoading = false;
  configSaving = false;
  configSaveResult: string | null = null;

  newKeyword = '';
  newIncludeTerm = '';
  newExcludeTerm = '';

  readonly intervalOptions = [
    { label: '1 hour',  value: 3600000 },
    { label: '2 hours', value: 7200000 },
    { label: '4 hours', value: 14400000 },
    { label: '8 hours', value: 28800000 },
    { label: '24 hours', value: 86400000 },
  ];

  private refreshInterval: any = null;

  get vPageStart(): number { return this.videoTotal === 0 ? 0 : this.videoPage * this.pageSize + 1; }
  get vPageEnd(): number   { return Math.min((this.videoPage + 1) * this.pageSize, this.videoTotal); }
  get cPageStart(): number { return this.commentTotal === 0 ? 0 : this.commentPage * this.pageSize + 1; }
  get cPageEnd(): number   { return Math.min((this.commentPage + 1) * this.pageSize, this.commentTotal); }

  ngOnInit(): void {
    void this.loadVideos();
    void this.loadComments();
    void this.loadConfig();
    this.refreshInterval = setInterval(() => {
      if (this.activeTab === 'videos') void this.loadVideos(false);
      else if (this.activeTab === 'comments') void this.loadComments(false);
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  switchTab(tab: 'videos' | 'comments' | 'config'): void {
    this.activeTab = tab;
    if (tab === 'config') void this.loadConfig();
  }

  // ── Video filters ────────────────────────────────────────────────────────

  applyVideoFilters(): void { this.videoPage = 0; void this.loadVideos(); }
  clearVideoFilters(): void {
    this.vFilterStatus = ''; this.vFilterSentiment = ''; this.vFilterRisk = '';
    this.vFilterKeyword = ''; this.vFilterFrom = ''; this.vFilterTo = ''; this.vFilterQ = '';
    this.vFilterWhitelisted = ''; this.vFilterBlacklisted = '';
    this.videoPage = 0; void this.loadVideos();
  }

  applyCommentFilters(): void { this.commentPage = 0; void this.loadComments(); }
  clearCommentFilters(): void {
    this.cFilterStatus = ''; this.cFilterSentiment = ''; this.cFilterIntent = '';
    this.cFilterRisk = ''; this.cFilterVideoId = ''; this.cFilterFrom = ''; this.cFilterTo = ''; this.cFilterQ = '';
    this.commentPage = 0; void this.loadComments();
  }

  filterByVideo(videoId: string): void {
    this.cFilterVideoId = videoId;
    this.activeTab = 'comments';
    this.commentPage = 0;
    void this.loadComments();
  }

  showWhitelisted(): void {
    this.vFilterWhitelisted = 'true';
    this.vFilterBlacklisted = '';
    this.videoPage = 0;
    void this.loadVideos();
  }

  showBlacklisted(): void {
    this.vFilterBlacklisted = 'true';
    this.vFilterWhitelisted = '';
    this.videoPage = 0;
    void this.loadVideos();
  }

  prevVideos(): void { if (this.videoPage > 0) { this.videoPage--; void this.loadVideos(); } }
  nextVideos(): void { if (this.videoPage < this.videoTotalPages - 1) { this.videoPage++; void this.loadVideos(); } }
  prevComments(): void { if (this.commentPage > 0) { this.commentPage--; void this.loadComments(); } }
  nextComments(): void { if (this.commentPage < this.commentTotalPages - 1) { this.commentPage++; void this.loadComments(); } }

  toggleVideo(id: number): void { this.expandedVideoId = this.expandedVideoId === id ? null : id; }
  toggleComment(id: number): void { this.expandedCommentId = this.expandedCommentId === id ? null : id; }

  // ── Reanalyze ─────────────────────────────────────────────────────────────

  async reanalyzeComments(): Promise<void> {
    this.reanalyzing = true;
    this.reanalyzeResult = null;
    try {
      const res = await fetch(`${this.apiBase()}/comments/reanalyze`, {
        method: 'POST', headers: this.authHeaders(),
      });
      const json: any = await res.json();
      const data = json?.data ?? json;
      this.reanalyzeResult = `Reanalyzed ${data?.reanalyzed ?? 0} comments`;
      void this.loadComments(false);
    } catch (e: any) {
      this.reanalyzeResult = 'Reanalysis failed: ' + (e?.message ?? 'Unknown error');
    } finally {
      this.reanalyzing = false;
      setTimeout(() => { this.reanalyzeResult = null; }, 5000);
    }
  }

  // ── Sync ─────────────────────────────────────────────────────────────────

  async syncNow(): Promise<void> {
    this.syncing = true;
    this.syncResult = null;
    try {
      const res = await fetch(`${this.apiBase()}/sync`, {
        method: 'POST', headers: this.authHeaders(),
      });
      const json: any = await res.json();
      const data = json?.data ?? json;
      const skipped = (data?.skippedIrrelevant ?? 0) + (data?.skippedBlacklisted ?? 0);
      this.syncResult = `Synced: ${data?.videosStored ?? 0} videos, ${data?.commentsStored ?? 0} comments` +
                        (skipped ? ` (${skipped} filtered out)` : '');
      void this.loadVideos();
      void this.loadComments();
    } catch (e: any) {
      this.syncResult = 'Sync failed: ' + (e?.message ?? 'Unknown error');
    } finally {
      this.syncing = false;
    }
  }

  // ── Status ───────────────────────────────────────────────────────────────

  async updateVideoStatus(video: YtVideo, status: string, event: Event): Promise<void> {
    event.stopPropagation();
    await fetch(`${this.apiBase()}/videos/${video.id}/status`, {
      method: 'PUT', headers: this.authHeaders(), body: JSON.stringify({ status }),
    });
    void this.loadVideos(false);
  }

  async updateCommentStatus(comment: YtComment, status: string): Promise<void> {
    await fetch(`${this.apiBase()}/comments/${comment.id}/status`, {
      method: 'PUT', headers: this.authHeaders(), body: JSON.stringify({ status }),
    });
    void this.loadComments(false);
  }

  // ── Whitelist / Blacklist ─────────────────────────────────────────────────

  async toggleWhitelist(video: YtVideo, event: Event): Promise<void> {
    event.stopPropagation();
    const res = await fetch(`${this.apiBase()}/videos/${video.id}/whitelist`, {
      method: 'PUT', headers: this.authHeaders(),
    });
    if (res.ok) {
      const json: any = await res.json();
      const updated = json?.data ?? json;
      video.isWhitelisted = updated.isWhitelisted;
      video.isBlacklisted = updated.isBlacklisted;
    }
  }

  async toggleBlacklist(video: YtVideo, event: Event): Promise<void> {
    event.stopPropagation();
    const res = await fetch(`${this.apiBase()}/videos/${video.id}/blacklist`, {
      method: 'PUT', headers: this.authHeaders(),
    });
    if (res.ok) {
      const json: any = await res.json();
      const updated = json?.data ?? json;
      video.isWhitelisted = updated.isWhitelisted;
      video.isBlacklisted = updated.isBlacklisted;
      // Remove from list if now blacklisted and not filtering for blacklisted
      if (updated.isBlacklisted && this.vFilterBlacklisted !== 'true') {
        this.videos = this.videos.filter(v => v.id !== video.id);
        this.videoTotal = Math.max(0, this.videoTotal - 1);
      }
    }
  }

  // ── Config ────────────────────────────────────────────────────────────────

  async loadConfig(): Promise<void> {
    this.configLoading = true;
    try {
      const res = await fetch(`${this.apiBase()}/config`, { headers: this.authHeaders() });
      if (res.ok) {
        const json: any = await res.json();
        this.config = { ...this.config, ...(json?.data ?? json) };
      }
    } catch { /* keep defaults */ }
    finally { this.configLoading = false; }
  }

  async saveConfig(): Promise<void> {
    this.configSaving = true;
    this.configSaveResult = null;
    try {
      const res = await fetch(`${this.apiBase()}/config`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({
          keywords: this.config.keywords,
          regionCode: this.config.regionCode || null,
          maxResults: this.config.maxResults,
          fetchIntervalMs: this.config.fetchIntervalMs,
          includeTerms: this.config.includeTerms,
          excludeTerms: this.config.excludeTerms,
          enabled: this.config.enabled,
        }),
      });
      if (res.ok) {
        const json: any = await res.json();
        this.config = { ...this.config, ...(json?.data ?? json) };
        this.configSaveResult = 'Settings saved successfully';
      } else {
        this.configSaveResult = 'Failed to save settings';
      }
    } catch (e: any) {
      this.configSaveResult = 'Error: ' + (e?.message ?? 'Unknown');
    } finally {
      this.configSaving = false;
      setTimeout(() => { this.configSaveResult = null; }, 4000);
    }
  }

  // Keyword chip management
  addKeyword(): void {
    const kw = this.newKeyword.trim();
    if (kw && !this.config.keywords.includes(kw)) {
      this.config.keywords = [...this.config.keywords, kw];
    }
    this.newKeyword = '';
  }
  removeKeyword(kw: string): void {
    this.config.keywords = this.config.keywords.filter(k => k !== kw);
  }

  // Include terms chip management
  addIncludeTerm(): void {
    const t = this.newIncludeTerm.trim();
    if (t && !this.config.includeTerms.includes(t)) {
      this.config.includeTerms = [...this.config.includeTerms, t];
    }
    this.newIncludeTerm = '';
  }
  removeIncludeTerm(t: string): void {
    this.config.includeTerms = this.config.includeTerms.filter(x => x !== t);
  }

  // Exclude terms chip management
  addExcludeTerm(): void {
    const t = this.newExcludeTerm.trim();
    if (t && !this.config.excludeTerms.includes(t)) {
      this.config.excludeTerms = [...this.config.excludeTerms, t];
    }
    this.newExcludeTerm = '';
  }
  removeExcludeTerm(t: string): void {
    this.config.excludeTerms = this.config.excludeTerms.filter(x => x !== t);
  }

  intervalLabel(ms: number): string {
    return this.intervalOptions.find(o => o.value === ms)?.label ?? `${ms / 3600000}h`;
  }

  // ── CSS helpers ───────────────────────────────────────────────────────────

  riskClass(level: string | null): string {
    switch ((level || '').toUpperCase()) {
      case 'CRITICAL': return 'risk-critical';
      case 'HIGH':     return 'risk-high';
      case 'MEDIUM':   return 'risk-medium';
      default:         return 'risk-low';
    }
  }

  sentimentClass(label: string | null): string {
    switch ((label || '').toUpperCase()) {
      case 'POSITIVE': return 'sentiment-positive';
      case 'NEGATIVE': return 'sentiment-negative';
      default:         return 'sentiment-neutral';
    }
  }

  cleanLabel(value: string | null): string {
    if (!value) return '-';
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  scoreText(score: number | null): string {
    return typeof score === 'number' ? `${Math.round(score * 100)}%` : '-';
  }

  formatDate(iso: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // ── Data loaders ──────────────────────────────────────────────────────────

  private async loadVideos(showSpinner = true): Promise<void> {
    if (showSpinner) this.videosLoading = true;
    this.errorMessage = '';
    try {
      const body: any = { page: this.videoPage, size: this.pageSize };
      if (this.vFilterStatus)    body.status    = this.vFilterStatus;
      if (this.vFilterSentiment) body.sentiment = this.vFilterSentiment;
      if (this.vFilterRisk)      body.risk       = this.vFilterRisk;
      if (this.vFilterKeyword)   body.keyword   = this.vFilterKeyword;
      if (this.vFilterFrom)      body.from      = `${this.vFilterFrom}T00:00:00`;
      if (this.vFilterTo)        body.to        = `${this.vFilterTo}T23:59:59`;
      if (this.vFilterQ.trim())  body.q         = this.vFilterQ.trim();
      if (this.vFilterWhitelisted) body.whitelisted = this.vFilterWhitelisted === 'true';
      if (this.vFilterBlacklisted) body.blacklisted = this.vFilterBlacklisted === 'true';

      const res = await fetch(`${this.apiBase()}/videos`, {
        method: 'POST', headers: this.authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) { this.errorMessage = `HTTP ${res.status}`; return; }
      const json: any = await res.json();
      const payload = json?.data ?? json;
      this.videos          = payload?.videos ?? [];
      this.videoTotal      = payload?.totalElements ?? 0;
      this.videoTotalPages = payload?.totalPages ?? 0;
      if (payload?.stats) this.stats = { ...this.stats, ...payload.stats };
    } catch (e: any) {
      this.errorMessage = e?.message ?? 'Failed to load videos';
    } finally {
      this.videosLoading = false;
    }
  }

  private async loadComments(showSpinner = true): Promise<void> {
    if (showSpinner) this.commentsLoading = true;
    try {
      const body: any = { page: this.commentPage, size: this.pageSize };
      if (this.cFilterVideoId)   body.videoId   = this.cFilterVideoId;
      if (this.cFilterStatus)    body.status    = this.cFilterStatus;
      if (this.cFilterSentiment) body.sentiment = this.cFilterSentiment;
      if (this.cFilterIntent)    body.intent    = this.cFilterIntent;
      if (this.cFilterRisk)      body.risk      = this.cFilterRisk;
      if (this.cFilterFrom)      body.from      = `${this.cFilterFrom}T00:00:00`;
      if (this.cFilterTo)        body.to        = `${this.cFilterTo}T23:59:59`;
      if (this.cFilterQ.trim())  body.q         = this.cFilterQ.trim();

      const res = await fetch(`${this.apiBase()}/comments`, {
        method: 'POST', headers: this.authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) return;
      const json: any = await res.json();
      const payload = json?.data ?? json;
      this.comments          = payload?.comments ?? [];
      this.commentTotal      = payload?.totalElements ?? 0;
      this.commentTotalPages = payload?.totalPages ?? 0;
    } catch { this.comments = []; }
    finally { this.commentsLoading = false; }
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token') ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' }
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  private apiBase(): string {
    return `${environment.apiHost}/api/v1/youtube-monitoring`;
  }
}
